/** UI helpers for SPEC-MSL-001 master↔linked shipment field state (FR-014). */
import { CrudActions } from './constants';
import { ErrorCode } from './errorCode';

/** Aggregated link-validation failure from create/update (node `details.messages`). */
export function isMasterShipmentAggregatedValidationError(
  error: unknown
): error is { code: ErrorCode.MASTER_LINKED_SHIPMENT_VALIDATION_FAILED; details: { messages: string[] } } {
  const body = error as
    | { code?: string; details?: { messages?: unknown } }
    | null
    | undefined;
  return (
    body?.code === ErrorCode.MASTER_LINKED_SHIPMENT_VALIDATION_FAILED &&
    Array.isArray(body?.details?.messages)
  );
}

/**
 * BR-006 fields compared by backend master-link validation (node SPEC-MSL-001 FR-006).
 * MUST stay in sync with node spec BR-006 — update both repos when backend adds/removes fields.
 */
const MASTER_LINK_PARTY_DETAIL_KEYS = [
  'name',
  'companyName',
  'company_name',
  'address_1',
  'address1',
  'address_2',
  'address2',
  'city',
  'state',
  'zipCode',
  'zip',
  'countryCode',
  'country_code',
] as const;

/** Hawb pageMode values vary by entry path (`view` vs `View`, `edit` vs `Edit`). */
export function isHawbViewPageMode(pageMode: string): boolean {
  return (
    pageMode === 'view' ||
    pageMode === 'View' ||
    pageMode === 'bulk-view'
  );
}

export function isHawbEditPageMode(pageMode: string): boolean {
  return pageMode === 'edit' || pageMode === 'Edit';
}

export function isHawbAddPageMode(pageMode: string): boolean {
  return pageMode === 'add' || pageMode === 'Add';
}

export function isLinkedMasterShipment(masterShipmentId: unknown): boolean {
  const id = Number(masterShipmentId);
  return Number.isFinite(id) && id > 0;
}

/** Piece row fields used for PAC-18824 Routing tab master/linked detection. */
export interface MasterLinkPieceRow {
  hawbId: number;
  isMaster: boolean;
  master_shipment?: number | null;
}

export function isNullOrZeroMasterShipmentRef(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  const id = Number(value);
  return !Number.isFinite(id) || id === 0;
}

/** Master piece row: `isMaster === true` and `master_shipment` null/0/empty. */
export function isMasterPieceRow(
  piece: Pick<MasterLinkPieceRow, 'isMaster' | 'master_shipment'>
): boolean {
  return piece.isMaster === true && isNullOrZeroMasterShipmentRef(piece.master_shipment);
}

/** Linked piece row: `isMaster === false` and positive `master_shipment`. */
export function isLinkedPieceRow(
  piece: Pick<MasterLinkPieceRow, 'isMaster' | 'master_shipment'>
): boolean {
  return piece.isMaster === false && isLinkedMasterShipment(piece.master_shipment);
}

export function findPieceForHawb(
  pieces: MasterLinkPieceRow[] | null | undefined,
  hawbId: number | null | undefined
): MasterLinkPieceRow | undefined {
  if (!pieces?.length || hawbId == null || !Number.isFinite(hawbId) || hawbId <= 0) {
    return undefined;
  }
  return pieces.find((piece) => Number(piece.hawbId) === hawbId);
}

/** True when at least one linked child references the master hawb id. */
export function hasLinkedChildPieces(
  pieces: MasterLinkPieceRow[] | null | undefined,
  masterHawbId: number | null | undefined
): boolean {
  if (
    !pieces?.length ||
    masterHawbId == null ||
    !Number.isFinite(masterHawbId) ||
    masterHawbId <= 0
  ) {
    return false;
  }
  return pieces.some(
    (piece) =>
      piece.isMaster === false &&
      Number(piece.master_shipment) === masterHawbId
  );
}

/**
 * Linked hawb for Routing tab: piece row first; fall back to hawb-level
 * `masterShipmentId` only when the piece row is missing (BR-MASLINK-UI-004).
 */
export function isCurrentHawbLinkedForRouting(
  pieces: MasterLinkPieceRow[] | null | undefined,
  hawbId: number | null | undefined,
  masterShipmentIdFallback?: unknown
): boolean {
  const piece = findPieceForHawb(pieces, hawbId);
  if (piece) {
    return isLinkedPieceRow(piece);
  }
  return isLinkedMasterShipment(masterShipmentIdFallback);
}

/**
 * Master with linked children, or current hawb is linked (FR-MASLINK-UI-006).
 * Standalone shipments return false.
 */
export function isInMasterLinkedShipmentGroup(
  pieces: MasterLinkPieceRow[] | null | undefined,
  currentHawbId: number | null | undefined,
  masterShipmentIdFallback?: unknown
): boolean {
  if (
    isCurrentHawbLinkedForRouting(
      pieces,
      currentHawbId,
      masterShipmentIdFallback
    )
  ) {
    return true;
  }
  return hasLinkedChildPieces(pieces, currentHawbId ?? null);
}

/**
 * Effective piece count for master-with-children detection.
 * Uses the higher of NgXS store count vs API payload count to avoid a flash of
 * editable state before the pieces subscription emits (FR-014 / AC-010).
 */
export function resolveMasterLinkPieceCount(
  storePieceCount: number,
  apiPieceCount?: number
): number {
  const fromStore = Number.isFinite(storePieceCount) ? storePieceCount : 0;
  const fromApi =
    typeof apiPieceCount === 'number' && apiPieceCount >= 0 ? apiPieceCount : 0;
  return Math.max(fromStore, fromApi);
}

/** Standalone master with at least one linked child (pieces include self + children). */
export function isMasterWithLinkedChildren(
  masterShipmentId: unknown,
  pieceCount: number,
  currentHawbId?: unknown
): boolean {
  const hawbId = Number(currentHawbId);
  if (!Number.isFinite(hawbId) || hawbId <= 0) {
    return false;
  }
  return !isLinkedMasterShipment(masterShipmentId) && pieceCount > 1;
}

export function shouldShowMasterShipmentViewHyperlink(
  pageMode: string,
  isLinked: boolean,
  hasMasterShipmentExt: boolean
): boolean {
  return isHawbViewPageMode(pageMode) && isLinked && hasMasterShipmentExt;
}

/** True when the party has no BR-006 detail (id/type/action-only payload stub). */
export function isConsigneeShipperStubParty(entity: unknown): boolean {
  if (entity == null || typeof entity !== 'object') {
    return true;
  }
  const party = entity as Record<string, unknown>;
  return !MASTER_LINK_PARTY_DETAIL_KEYS.some((field) => {
    const value = party[field];
    return value != null && String(value).trim() !== '';
  });
}

/**
 * Drops consignee/shipper objects that carry only CRUD metadata (no address fields).
 * Backend link validation treats those stubs as empty parties and rejects when the
 * master has no matching hawb_roles row.
 */
export function removeStubPartiesFromConsigneeShipper(
  consigneeShipper: { consignee?: unknown; shipper?: unknown } | null | undefined
): void {
  if (!consigneeShipper) {
    return;
  }
  if (
    consigneeShipper.consignee &&
    (consigneeShipper.consignee as { action?: string }).action !==
      CrudActions.delete &&
    isConsigneeShipperStubParty(consigneeShipper.consignee)
  ) {
    delete consigneeShipper.consignee;
  }
  if (
    consigneeShipper.shipper &&
    (consigneeShipper.shipper as { action?: string }).action !==
      CrudActions.delete &&
    isConsigneeShipperStubParty(consigneeShipper.shipper)
  ) {
    delete consigneeShipper.shipper;
  }
}